import { Task } from "@common-models/task";

import MediaManagerReducer from "@common-reducers/MediaManagerReducer";
import ModalReducer from "@common-reducers/ModalReducer";
import UndoRedoReducer from "@common-reducers/UndoRedoReducer";
import UninstallReducer from "@common-reducers/UninstallReducer";
import {
  configureStore
} from "@reduxjs/toolkit";

import { isBackground, isExtensionContext, isInPopup, isWebPageButNotWhatsappPage, isWhatsappExtensionContext } from "@common-services/detect-context";

import { perfLogger, PerformanceCategory } from "@common/utils/performance-logger";
import { lightweightPerformanceMiddleware } from "@common/middleware/performance-middleware";
import { installGlobalErrorCapture, setUiStallSink, watchPageBridgeAnswered } from "@common/services/ui-stall-reporter";
import { hasPageBridgeAnswered } from "@common/services/post-message-to-whatsapp-functions";

import AccessFormPopupReducer from "@common-reducers/AccessFormPopupReducer";

import AddScheduledMessageButtonReducer from "@common-reducers/AddScheduledMessageButtonReducer";
import SubscriptionHydrationReducer from "@common-reducers/subscription-hydration";


import backendApi, {
  backendApiEndpoints,
  initAppData,
  middleware
} from "@common-reducers/backend-api/backend-api";


import BoardsReducer from "@common-reducers/BoardsReducer";
import BoardViewReducer from "@common-reducers/BoardviewReducers";
import BulkActionsReducer from "@common-reducers/BulkActionsReducer";
import CampaignReducer from "@common-reducers/CampaignReducer";
import ChatListReducer from "@common-reducers/ChatListReducer";
import { ContactTableReducer } from "@common-reducers/ContactTableReducer";
import CreditUsageReducer from "@common-reducers/CreditUsageReducer";
import {
  DBAllocatedPhoneNumberReducer,
  DBAutomationSessionReducer,
  DBBoardsReducer,
  DBBoardUserReducer,
  DBBoardViewReducer,
  DBBotDeploymentReducer,
  DBBotReducer,
  DBKnowledgeBaseReducer,
  DBBulkListReducer,
  DBCampaignReducer,
  DBDripReducer,
  DBDripTemplateReducer,
  DBCampaignLogReducer,
  DBContactReducer,
  DBContactsLabelsReducer,
  DBEntitySubscribersReducer,
  DBFeatureReducer,
  DBKanbanReducer,
  DBLabelReducer,
  DBLogReducer,
  DBMessageLogReducer,
  DBMyWorksReducer,
  DBNewTabFocusReducer,
  DBNodeEventsReducer,
  DBNotificationsReducer,
  DBQuickLaunchReducer,
  DBSessionReducer,
  DBSubscriptionReducer, DBEmailSendReducer, DBTaskColumnOptionReducer, DBTaskColumnReducer, DBTaskgroupsReducer,
  DBTasksExtraDataFilesReducer,
  DBTasksExtraDataReducer,
  DBTasksReducer,
  DBTeamInboxChatsCacheReducer,
  DBTeamInboxMessagesCacheReducer,
  DBTemplateReducer,
  DBTimeTrackingSessionReducer,
  DBTracingsReducer,
  DBUserMessageReducer,
  DBUserReducer,
  DBViewFiltersReducer,
  DBWaChatReducer,
  DBWaContactsCacheReducer,
  DBWaNoteReducer,
  DBWebhooksReducer,
  DBWorkloadReducer,
  DBWorkspaceReducer,
  DBWorkspaceUserReducer,
  DBRemindersReducer,
  DBCustomPromptsReducer
} from "@common-reducers/DBServiceThunks";
import ExtraDataReducer from "@common-reducers/ExtraDataReducer";
import ImportItemsReducer from "@common-reducers/ImportItemsReducer";
import KanbanReducer from "@common-reducers/KanbanReducers";
import LabelsReducer from "@common-reducers/LabelsReducer";
import MainReducer, {
  initStorageWrapper,
} from "@common-reducers/MainReducer";
import PickerDialogReducer from "@common-reducers/PickerDialogReducer";
import ProfilePicReducer from "@common-reducers/ProfilePicReducer";
import ShareDialogReducer from "@common-reducers/ShareDialogReducer";
import SnippetsReducer from "@common-reducers/SnippetsReducer";
import SubscriptionPopupReducer from "@common-reducers/SubscriptionPopupReducer";
import TasksReducer from "@common-reducers/TasksReducer";
import UserReducer from "@common-reducers/UserReducer";

import WhatsAppReducer from "@common-reducers/WhatsAppReducer";
import WorkspaceReducer from "@common-reducers/WorkspaceReducer";

import NotifierReducer from "@/common/noifier-module/NotifierReducer";
import connectedEnginesReducer from "@common/reducers/ConnectedEnginesSlice";
import BtQuickPanelReducer from "@common-reducers/BtQuickPanelReducer";
import CalendarReducer from "@common-reducers/CalendarReducer";
import PlannerSentHistoryReducer from "@common-reducers/planner-sent-history";
import LabelReducer from "@common-reducers/labels-reducer";
import MyWorkReducer from "@common-reducers/MyWorkReducer";
import RecycleBinReducer from "@common-reducers/RecycleBinReducer";
import StickyHeaderReducer from "@common-reducers/StickyHeaderReducer";
import WhatsAppViewReducer from "@common-reducers/WhatsAppViewReducer";
import { initAuth } from "@/common/bt-auth/AuthThunks";
import ChatbotReducer from "@common/reducers/ChatbotReducer";
import ConnectionReadinessSlice from "@common/reducers/ConnectionReadinessSlice";
import knowledgeBaseProgressReducer from "@common/reducers/KnowledgeBaseProgressSlice";
import ScheduledMessageEditorReducer from "@common/reducers/ScheduledMessageEditorReducer";
import UserMessageReducer from "@common/reducers/UserMessageReducer";
import MessageInfoReducer from "@common/reducers/MessageInfoReducer";
import BtLayoutSidebarReducer from "@common/reducers/BtLayoutSidebarReducer";
import UpdateNotificationReducer from "@common/reducers/UpdateNotificationReducer";
import { AppThunkDispatch } from "@common/types/common-root-state-type";
import RemindersUIReducer from "@common/reducers/RemindersUIReducer";
import MessageDebugReducer from "@common/reducers/MessageDebugReducer";
import BlueticksPanelReducer from "@common/reducers/BlueticksPanelReducer";
import DisplaySettingsReducer from "@common/reducers/DisplaySettingsReducer";
import EnhancementsReducer from "@common/reducers/EnhancementsReducer";
import TranscriptionPrefsReducer from "@common/reducers/TranscriptionPrefsReducer";
import NudgeReducer from "@common/reducers/nudge-slice";
import DripEditorReducer from "@common/reducers/DripEditorReducer";
import { FreeLimitReducer } from "@common/reducers/free-limit-slice";



let reducerMap = {
  DBSessionReducer,
  DBBulkListReducer,
  DBContactReducer,
  DBTemplateReducer,
  DBLabelReducer,
  DBCampaignReducer,
  DBDripReducer,
  DBDripTemplateReducer,
  DBCampaignLogReducer,
  DBTaskgroupsReducer,
  DBTasksExtraDataReducer,
  DBTasksExtraDataFilesReducer,
  DBMessageLogReducer,
  DBWaChatReducer,
  DBWaNoteReducer,
  DBLogReducer,
  DBViewFiltersReducer,
  DBBoardsReducer,
  DBTasksReducer,
  DBTaskColumnReducer,
  DBTaskColumnOptionReducer,
  DBUserReducer,
  DBWorkspaceReducer,
  DBKanbanReducer,
  DBBoardViewReducer,
  DBFeatureReducer,
  DBMyWorksReducer,
  DBWorkloadReducer,
  DBWorkspaceUserReducer,
  DBCustomPromptsReducer,
  DBBoardUserReducer,
  DBSubscriptionReducer,
  DBEmailSendReducer,
  DBTimeTrackingSessionReducer,
  DBNewTabFocusReducer,
  DBWebhooksReducer,
  DBAllocatedPhoneNumberReducer,
  // Presence tracking (upsertClient/removeClient from MQTT presence topics).
  // Without this reducer registered, handlePresenceMessage's dispatches were
  // silently dropped in the WA tab and the team-inbox-open event gate never saw
  // any sockets. The website store (web-store.ts) registers it too.
  ConnectedEnginesReducer: connectedEnginesReducer,
  UndoRedoReducer,
  ChatListReducer,
  TasksReducer,
  MyWorkReducer,
  KanbanReducer,
  BoardViewReducer,
  BulkActionsReducer,
  BoardsReducer,
  WhatsAppReducer,
  PickerDialogReducer,
  MainReducer,
  DBTracingsReducer,
  MediaManagerReducer,
  ExtraDataReducer,
  ShareDialogReducer,
  LabelsReducer,
  SubscriptionPopupReducer,
  CampaignReducer,
  UserReducer,
  ChatbotReducer,
  CalendarReducer,
  PlannerSentHistoryReducer,
  ContactTableReducer,
  DBUserMessageReducer,
  ProfilePicReducer,
  SnippetsReducer,
  AddScheduledMessageButtonReducer,
  SubscriptionHydrationReducer,
  AccessFormPopupReducer,
  backendApi,
  UninstallReducer,
  ModalReducer,
  ImportItemsReducer,
  WorkspaceReducer,
  WhatsAppViewReducer,
  NotifierReducer,
  DBQuickLaunchReducer,
  BtQuickPanelReducer,
  RecycleBinReducer,
  LabelReducer,
  DBEntitySubscribersReducer,
  DBNotificationsReducer,
  DBContactsLabelsReducer,
  StickyHeaderReducer,
  DBBotReducer,
  DBKnowledgeBaseReducer,
  DBBotDeploymentReducer,
  DBAutomationSessionReducer,
  DBNodeEventsReducer,
  DBWaContactsCacheReducer,
  DBTeamInboxChatsCacheReducer,
  DBTeamInboxMessagesCacheReducer,
  UserMessageReducer,
  ConnectionReadinessSlice,
  CreditUsageReducer,
  KnowledgeBaseProgressReducer: knowledgeBaseProgressReducer,
  ScheduledMessageEditorReducer,
  MessageInfoReducer,
  BtLayoutSidebarReducer,
  UpdateNotificationReducer,
  DBRemindersReducer,
  RemindersUIReducer,
  MessageDebugReducer,
  BlueticksPanelReducer,
  DisplaySettingsReducer,
  EnhancementsReducer,
  TranscriptionPrefsReducer,
  NudgeReducer,
  DripEditorReducer,
  FreeLimitReducer,
};

if (isBackground()) {
  reducerMap = {} as any;
}

const ignoreLargePathList = [
  "WhatsAppReducer.contactList",
  "WhatsAppReducer.contactMap",
];

perfLogger.group('Extension Store Configuration');
const storeTimerId = perfLogger.start(PerformanceCategory.REDUX, 'Configure Redux Store');

const store = configureStore({
  /* Uncomment this to debug extension */
  // devTools: false,
  // enhancers,
  reducer: reducerMap,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // immutableCheck is expensive — dev builds only (staging/prod skip it)
      immutableCheck: process.env.NODE_ENV === 'development'
        ? { ignoredPaths: ignoreLargePathList }
        : false,
      serializableCheck: false,
    })
    .concat(middleware)
    .concat(lightweightPerformanceMiddleware), // Add performance logging
});

// #651: route throttled dead-click diagnostics through the existing events pipeline.
setUiStallSink((props) => {
  void store.dispatch(backendApiEndpoints.recordUiDiagnosticEvent.initiate({ name: 'ui_action_stalled', props: { ...props } }));
});
// #651: remember the last extension error so a mount_timeout can say what threw just before it,
// and report a page bridge that never answers (silent since #764 fix 4).
// WhatsApp page only — the web app bundles this store and has no page bridge or overlay to watch.
if (typeof window !== 'undefined' && isWhatsappExtensionContext()) {
  installGlobalErrorCapture(window);
  watchPageBridgeAnswered(hasPageBridgeAnswered);
}

perfLogger.end(storeTimerId, {
  reducerCount: Object.keys(reducerMap).length,
  middlewareCount: 'multiple'
});
perfLogger.takeMemorySnapshot('After Redux Store Init');
perfLogger.groupEnd();

console.log('[ExtensionStore] Redux store configured with performance logging');

export interface ScheduleTaskData {
  task: Task;
  to: string;
}

export default store;
export const dispatch: AppThunkDispatch = store.dispatch;
export type ExtensionRootState = ReturnType<typeof store.getState>;
/* init app functions */

console.log("init: isExtensionContext", isExtensionContext());
console.log("init: isWebPage", isWebPageButNotWhatsappPage());
console.log("init: isInPopup", isInPopup());


if (isExtensionContext() || isWebPageButNotWhatsappPage() || isInPopup()) {

  console.log("init: app functions");

  dispatch(initAuth());
  dispatch(initAppData());
  dispatch(initStorageWrapper());
}
